import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePermissions, PERMISSIONS } from '../contexts/PermissionsContext';
import { ArrowLeft, Download, History, AlertTriangle, Check, Eye, Zap, FileSignature, FileText, ChevronLeft, ChevronRight, X, RefreshCw, AlertCircle, XCircle } from 'lucide-react';
import DocumentAccessControl from '../components/DocumentAccessControl';
import SignatureAuthHandler from '../components/SignatureAuthHandler';
import { documentService } from '../services/documentService';
import { API_URL } from '../utils/apiConfig';
import axios from 'axios';

// Import our fixed components
import DocumentViewer from '../components/DocumentViewer';
import DocumentEditor from '../components/DocumentEditor';
import DocumentVersionHistory from '../components/DocumentVersionHistory';
import { Document, Page } from 'react-pdf';
import { findTextLocation } from '../api/documents';

const DocumentPage = ({ publicMode = false, mode = 'viewer' }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasDocumentPermission } = usePermissions();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAccessControl, setShowAccessControl] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [hasSignatureToken, setHasSignatureToken] = useState(false);
  const [, setBackendPermissions] = useState(null);
  const [redliningStatus, setRedliningStatus] = useState(null);
  const [showRedliningPrompt, setShowRedliningPrompt] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showRedliningNotice, setShowRedliningNotice] = useState(false);
  const [annotationRefreshTrigger, setAnnotationRefreshTrigger] = useState(0);
  const [showSignatures, setShowSignatures] = useState(false);
  const [showSignatureRequests, setShowSignatureRequests] = useState(false);
  const [signatureFieldsInfo, setSignatureFieldsInfo] = useState(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const headerRef = useRef(null);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [isVoiding, setIsVoiding] = useState(false);
  
  // Check for signature token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    setHasSignatureToken(!!token);
  }, [searchParams]);

  // Add scroll detection to track header visibility using ref
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        try {
          const headerRect = headerRef.current.getBoundingClientRect();
          // Consider header visible if any part of it is visible
          const visible = headerRect.bottom > 0;
          setIsHeaderVisible(visible);
        } catch (error) {
          // Silently handle any errors during scroll detection
          console.warn('Error in scroll handler:', error);
        }
      }
    };

    // Add throttling to prevent excessive calls
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    
    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, []);
  
  // If in public mode, always grant view permission
  // If has signature token, use enhanced authentication flow
  const canView = publicMode ? true : (hasSignatureToken ? true : hasDocumentPermission(id, PERMISSIONS.VIEW));
  const canEdit = publicMode ? false : (hasSignatureToken ? false : hasDocumentPermission(id, PERMISSIONS.EDIT));
  const canSign = publicMode ? false : (hasSignatureToken ? true : hasDocumentPermission(id, PERMISSIONS.SIGN));
  const canShare = publicMode ? false : (hasSignatureToken ? false : hasDocumentPermission(id, PERMISSIONS.SHARE));
  
  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        
        // Get signature token from URL if present
        const token = searchParams.get('token');
        
        // Use different API endpoint based on mode and token
        let data;
        if (publicMode) {
          data = await documentService.getPublicDocument(id);
        } else if (token) {
          data = await documentService.getDocument(id, token);
        } else {
          data = await documentService.getDocument(id);
        }
          
        setDocument(data);
        
        // 🔒 Extract backend-validated permissions (authoritative)
        if (data && !publicMode) {
          const permissions = {
            [PERMISSIONS.VIEW]: true, // If we got the document, we can view it
            [PERMISSIONS.EDIT]: data.isOwner || data.permission === 'edit' || data.permission === 'owner',
            [PERMISSIONS.SIGN]: data.isOwner || data.permission === 'sign' || data.permission === 'edit' || data.permission === 'owner',
            [PERMISSIONS.SHARE]: data.isOwner || data.permission === 'share' || data.permission === 'owner',
            [PERMISSIONS.ADMIN]: data.isOwner || data.permission === 'owner'
          };
          setBackendPermissions(permissions);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching document:', error);
        setLoading(false);
        // Handle 404 or permissions errors - backend already enforced security
        if (error.response && error.response.status === 404) {
          alert('Document not found');
          navigate('/');
        } else if (error.response && error.response.status === 403) {
          alert('You don\'t have permission to view this document');
          navigate('/');
        }
      }
    };
    
    fetchDocument();
  }, [id, navigate, publicMode, searchParams]);

  // Check redlining status when document is loaded
  useEffect(() => {
    const checkRedliningStatus = async () => {
      if (!document || !id) return;
      
      try {
        const token = searchParams.get('token');
        let apiUrl = `${API_URL}/documents/${id}/redlining/status`;
        
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        } else if (localStorage.getItem('token')) {
          headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
        }
        
        const response = await axios.get(apiUrl, { headers });
        setRedliningStatus(response.data);
        
        // Show redlining prompt if AI analysis is recommended and user can sign
        if (response.data.recommendAiAnalysis && canSign && !response.data.aiAnalysisCompleted) {
          setShowRedliningNotice(true);
        }
      } catch (error) {
        console.error('Error checking redlining status:', error);
      }
    };
    
    checkRedliningStatus();
  }, [document, id, canSign, searchParams]);
  
  // 🔒 Security Note: Removed client-side redirect logic
  // Backend already enforces permissions and returns 403 if unauthorized
  // Client-side permissions are now used for UX optimization only
  
  const handleSaveDocument = async (updatedDocument) => {
    try {
      // In a real implementation, you would call an API to save the document
      // For demo, we'll just update the local state
      setDocument(prev => ({
        ...prev,
        version: updatedDocument.version
      }));
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving document:', error);
    }
  };
  
  const handleRestoreVersion = async (versionId, versionNumber) => {
    try {
      // In a real implementation, you would call an API to restore the version
      // For demo, we'll just update the local state
      setDocument(prev => ({
        ...prev,
        version: versionNumber
      }));
      
      setShowVersionHistory(false);
    } catch (error) {
      console.error('Error restoring version:', error);
    }
  };

  const handleDownloadDocument = async () => {
    try {
      console.log('📄 Downloading document with embedded signatures...');
      
      const token = searchParams.get('token');
      let fileUrl = `${API_URL}/documents/${id}/download`;
      
      const headers = {};
      if (token) {
        fileUrl += `?token=${encodeURIComponent(token)}`;
      } else if (localStorage.getItem('token')) {
        headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
      }
      
      // Fetch the file as a blob
      const response = await axios.get(fileUrl, {
        responseType: 'blob',
        headers
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      
      // Create a temporary download link
      const link = window.document.createElement('a');
      link.href = blobUrl;
      const filename = document?.originalFileName || document?.name || 'document';
      link.download = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      
      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
      
      console.log('✅ Document downloaded successfully');
      
    } catch (error) {
      console.error('❌ Error downloading document:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  const handleVoidDocument = async () => {
    if (!voidReason.trim()) {
      alert('Please provide a reason for voiding the document');
      return;
    }

    try {
      setIsVoiding(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/documents/${id}/void`,
        { reason: voidReason },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data) {
        alert('Document has been voided successfully. All parties have been notified.');
        setShowVoidModal(false);
        setVoidReason('');
        // Force a hard refresh to clear all caches and show voided status
        window.location.href = window.location.href.split('?')[0] + '?refresh=' + Date.now();
      }
    } catch (error) {
      console.error('Error voiding document:', error);
      alert(error.response?.data?.error || 'Failed to void document');
    } finally {
      setIsVoiding(false);
    }
  };

  const handleAiAnalysis = async () => {
    try {
      setIsAnalyzing(true);

      const token = searchParams.get('token');
      let apiUrl = `${API_URL}/documents/${id}/redlining/ai-analyze`;

      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else if (localStorage.getItem('token')) {
        headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      }

      await axios.post(apiUrl, {}, { headers });

      // Refresh redlining status after analysis
      const statusResponse = await axios.get(`${API_URL}/documents/${id}/redlining/status`, { headers });
      setRedliningStatus(statusResponse.data);

      setShowRedliningPrompt(false);
      setShowRedliningNotice(false);

      // Trigger annotation refresh in DocumentViewer
      setAnnotationRefreshTrigger(prev => prev + 1);

      // Show success message
      alert('AI analysis completed! Review the highlighted areas before signing.');

    } catch (error) {
      console.error('Error during AI analysis:', error);
      alert('Failed to analyze document. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };


  // Calculate remaining fields for current user (signatures + form fields)
  const calculateRemainingFields = () => {
    if (!signatureFieldsInfo || !signatureFieldsInfo.user) return 0;
    
    const currentUserFields = signatureFieldsInfo.currentUserFields || [];
    console.log('DEBUG: calculateRemainingFields - currentUserFields:', currentUserFields);
    
    const uncompletedFields = currentUserFields.filter(field => {
      // Handle signature fields
      if (field.type !== 'form_field') {
        // Check if field is signed (finalized)
        const isFinalized = signatureFieldsInfo.isFieldSigned(field.id);
        
        // Check if field has applied signature (signed but not finalized)
        const hasAppliedSignature = signatureFieldsInfo.appliedSignatures && 
          signatureFieldsInfo.appliedSignatures.some(sig => sig.fieldId === field.id);
        
        // Field is considered "done" if it's either finalized OR has an applied signature
        const isNotDone = !isFinalized && !hasAppliedSignature;
        console.log(`DEBUG: Signature field ${field.id} - finalized: ${isFinalized}, hasApplied: ${hasAppliedSignature}, notDone: ${isNotDone}`);
        return isNotDone;
      } else {
        // Handle form fields - check if completed
        const isCompleted = signatureFieldsInfo.isFormFieldCompleted && 
          signatureFieldsInfo.isFormFieldCompleted(field.fieldId || field.id);
        
        console.log(`DEBUG: Form field ${field.fieldId || field.id} - completed: ${isCompleted}, fieldValue: ${field.fieldValue}`);
        return !isCompleted;
      }
    });
    
    console.log('DEBUG: calculateRemainingFields - uncompletedFields:', uncompletedFields);
    return uncompletedFields.length;
  };

  // Navigate to next required field (signatures + form fields)
  const navigateToNextField = () => {
    if (!signatureFieldsInfo || !signatureFieldsInfo.user) return;
    
    const currentUserFields = signatureFieldsInfo.currentUserFields || [];
    const nextField = currentUserFields.find(field => {
      // Handle signature fields
      if (field.type !== 'form_field') {
        // Check if field is signed (finalized)
        const isFinalized = signatureFieldsInfo.isFieldSigned(field.id);
        
        // Check if field has applied signature (signed but not finalized)
        const hasAppliedSignature = signatureFieldsInfo.appliedSignatures && 
          signatureFieldsInfo.appliedSignatures.some(sig => sig.fieldId === field.id);
        
        // Field is available for signing if it's neither finalized nor has applied signature
        return !isFinalized && !hasAppliedSignature;
      } else {
        // Handle form fields - check if completed
        const isCompleted = signatureFieldsInfo.isFormFieldCompleted && 
          signatureFieldsInfo.isFormFieldCompleted(field.fieldId || field.id);
        
        return !isCompleted;
      }
    });
    
    if (nextField && signatureFieldsInfo.onNavigateToField) {
      signatureFieldsInfo.onNavigateToField(nextField);
      // Close signature requests panel after navigation
      setShowSignatureRequests(false);
    }
  };
  
  // For signature token flows, wrap everything with SignatureAuthHandler
  if (hasSignatureToken && !publicMode) {
    return (
      <SignatureAuthHandler documentId={id}>
        <DocumentPageContent
          document={document}
          loading={loading}
          canView={canView}
          canEdit={canEdit}
          canSign={canSign}
          canShare={canShare}
          showAccessControl={showAccessControl}
          setShowAccessControl={setShowAccessControl}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          showVersionHistory={showVersionHistory}
          setShowVersionHistory={setShowVersionHistory}
          handleSaveDocument={handleSaveDocument}
          handleRestoreVersion={handleRestoreVersion}
          handleDownloadDocument={handleDownloadDocument}
          handleVoidDocument={handleVoidDocument}
          showVoidModal={showVoidModal}
          setShowVoidModal={setShowVoidModal}
          voidReason={voidReason}
          setVoidReason={setVoidReason}
          isVoiding={isVoiding}
          navigate={navigate}
          id={id}
          hasSignatureToken={hasSignatureToken}
          redliningStatus={redliningStatus}
          showRedliningPrompt={showRedliningPrompt}
          setShowRedliningPrompt={setShowRedliningPrompt}
          showRedliningNotice={showRedliningNotice}
          setShowRedliningNotice={setShowRedliningNotice}
          isAnalyzing={isAnalyzing}
          handleAiAnalysis={handleAiAnalysis}
          annotationRefreshTrigger={annotationRefreshTrigger}
          showSignatures={showSignatures}
          setShowSignatures={setShowSignatures}
          showSignatureRequests={showSignatureRequests}
          setShowSignatureRequests={setShowSignatureRequests}
          signatureFieldsInfo={signatureFieldsInfo}
          setSignatureFieldsInfo={setSignatureFieldsInfo}
          calculateRemainingFields={calculateRemainingFields}
          navigateToNextField={navigateToNextField}
          isHeaderVisible={isHeaderVisible}
          headerRef={headerRef}
          mode={mode}
        />
      </SignatureAuthHandler>
    );
  }

  // For regular flows, render directly  
  return (
    <DocumentPageContent
      document={document}
      loading={loading}
      canView={canView}
      canEdit={canEdit}
      canSign={canSign}
      canShare={canShare}
      showAccessControl={showAccessControl}
      setShowAccessControl={setShowAccessControl}
      isEditing={isEditing}
      setIsEditing={setIsEditing}
      showVersionHistory={showVersionHistory}
      setShowVersionHistory={setShowVersionHistory}
      handleSaveDocument={handleSaveDocument}
      handleRestoreVersion={handleRestoreVersion}
      handleDownloadDocument={handleDownloadDocument}
      handleVoidDocument={handleVoidDocument}
      showVoidModal={showVoidModal}
      setShowVoidModal={setShowVoidModal}
      voidReason={voidReason}
      setVoidReason={setVoidReason}
      isVoiding={isVoiding}
      navigate={navigate}
      id={id}
      hasSignatureToken={hasSignatureToken}
      redliningStatus={redliningStatus}
      showRedliningPrompt={showRedliningPrompt}
      setShowRedliningPrompt={setShowRedliningPrompt}
      showRedliningNotice={showRedliningNotice}
      setShowRedliningNotice={setShowRedliningNotice}
      isAnalyzing={isAnalyzing}
      handleAiAnalysis={handleAiAnalysis}
      annotationRefreshTrigger={annotationRefreshTrigger}
      showSignatures={showSignatures}
      setShowSignatures={setShowSignatures}
      showSignatureRequests={showSignatureRequests}
      setShowSignatureRequests={setShowSignatureRequests}
      signatureFieldsInfo={signatureFieldsInfo}
      setSignatureFieldsInfo={setSignatureFieldsInfo}
      calculateRemainingFields={calculateRemainingFields}
      navigateToNextField={navigateToNextField}
      isHeaderVisible={isHeaderVisible}
      headerRef={headerRef}
      mode={mode}
    />
  );
};

// Extract the main content into a separate component
const DocumentPageContent = ({
  document,
  loading,
  canView,
  canEdit,
  canSign,
  canShare,
  showAccessControl,
  setShowAccessControl,
  isEditing,
  setIsEditing,
  showVersionHistory,
  setShowVersionHistory,
  handleSaveDocument,
  handleRestoreVersion,
  handleDownloadDocument,
  handleVoidDocument,
  showVoidModal,
  setShowVoidModal,
  voidReason,
  setVoidReason,
  isVoiding,
  navigate,
  id,
  hasSignatureToken,
  redliningStatus,
  showRedliningPrompt,
  setShowRedliningPrompt,
  showRedliningNotice,
  setShowRedliningNotice,
  isAnalyzing,
  handleAiAnalysis,
  annotationRefreshTrigger,
  showSignatures,
  setShowSignatures,
  showSignatureRequests,
  setShowSignatureRequests,
  signatureFieldsInfo,
  setSignatureFieldsInfo,
  calculateRemainingFields,
  navigateToNextField,
  isHeaderVisible,
  headerRef,
  mode = 'viewer',
}) => {
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-900">Document not found</div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <div ref={headerRef} className="document-header bg-white border-b border-gray-200 p-4">
        <div className="container mx-auto max-w-6xl">
          {/* Signature token indicator */}
          {hasSignatureToken && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-4 1-1-4 .257-.257A6 6 0 1118 8zm-6-2a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Signature Request:</strong> You've been invited to review and sign this document.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Redlining notice */}
          {showRedliningNotice && redliningStatus && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-yellow-800">
                    AI Analysis Recommended
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    {redliningStatus.analysisMessage}
                  </p>
                  <div className="mt-3 flex space-x-2">
                    <button
                      onClick={() => setShowRedliningPrompt(true)}
                      className="inline-flex items-center px-3 py-1.5 border border-yellow-300 text-sm font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                    >
                      <Zap className="h-4 w-4 mr-1" />
                      Start AI Analysis
                    </button>
                    <button
                      onClick={() => setShowRedliningNotice(false)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Skip for now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Redlining status indicator */}
          {redliningStatus && redliningStatus.aiAnalysisCompleted && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Check className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">
                    <strong>AI Analysis Complete:</strong> {redliningStatus.totalAnnotations} annotations found 
                    {redliningStatus.riskLevel && redliningStatus.riskLevel !== 'unknown' && (
                      <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                        redliningStatus.riskLevel === 'low' ? 'bg-green-100 text-green-800' :
                        redliningStatus.riskLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {redliningStatus.riskLevel.toUpperCase()} RISK
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    Scroll through the document to see highlighted areas and comments from AI analysis
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Voided Document Banner */}
          {document?.status === 'voided' && (
            <div className="mb-4 p-4 bg-red-50 border-2 border-red-500 rounded-md">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XCircle className="h-6 w-6 text-red-500" />
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-semibold text-red-800">
                    This Document Has Been Voided
                  </h3>
                  <p className="text-sm text-red-700 mt-1">
                    This document was voided on {document.voidedAt ? new Date(document.voidedAt).toLocaleDateString() : 'an unknown date'} and is no longer valid for signing.
                  </p>
                  {document.voidReason && (
                    <div className="mt-2 p-2 bg-white border border-red-200 rounded">
                      <p className="text-sm text-gray-700">
                        <strong>Reason:</strong> {document.voidReason}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button 
                onClick={() => navigate(hasSignatureToken ? '/' : '/dashboard')}
                className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold">{document?.name || 'Untitled Document'}</h1>
                {document?.version && (
                  <div className="text-sm text-gray-500">Version {document.version || 1}</div>
                )}
                {document?.status === 'voided' && (
                  <span className="ml-2 px-2 py-1 text-xs font-semibold bg-red-100 text-red-800 rounded-full">
                    VOIDED
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {!isEditing && !showVersionHistory && (
                <>

                  
                  
                  {/* Signature Requests Button */}
                  {(() => {
                    const remainingFields = calculateRemainingFields();
                    const hasFieldsToSign = remainingFields > 0;
                    
                    return (
                      <button
                        onClick={() => {
                          if (hasFieldsToSign) {
                            // Navigate to next field if there are fields left to sign
                            navigateToNextField();
                          } else {
                            // Show signature requests panel if no fields left or no field info
                            setShowSignatureRequests(!showSignatureRequests);
                            setShowSignatures(false);
                            setShowAccessControl(false);
                          }
                        }}
                        className="px-4 py-2 rounded-xl font-medium transition-all duration-300 ease-out border group flex items-center"
                        style={{
                          background: hasFieldsToSign 
                            ? 'linear-gradient(135deg, #1e40af, #1e3a8a)' 
                            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))',
                          color: hasFieldsToSign ? '#ffffff' : '#374151',
                          borderColor: hasFieldsToSign ? '#1e40af' : '#e5e7eb',
                          fontWeight: 500,
                          letterSpacing: '-0.01em',
                          boxShadow: hasFieldsToSign 
                            ? '0 2px 8px rgba(30, 64, 175, 0.15)' 
                            : '0 1px 3px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                          textShadow: hasFieldsToSign ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => {
                          if (hasFieldsToSign) {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 16px rgba(30, 64, 175, 0.2)';
                            e.target.style.filter = 'brightness(1.05)';
                          } else {
                            e.target.style.background = 'linear-gradient(135deg, rgba(248, 250, 252, 0.9), rgba(241, 245, 249, 0.8))';
                            e.target.style.color = '#1f2937';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (hasFieldsToSign) {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 8px rgba(30, 64, 175, 0.15)';
                            e.target.style.filter = 'brightness(1)';
                          } else {
                            e.target.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))';
                            e.target.style.color = '#374151';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
                          }
                        }}
                        title={hasFieldsToSign ? `Click to go to next field (${remainingFields} remaining)` : "View signature requests"}
                      >
                        <FileSignature size={16} className="mr-2 transition-transform duration-300 group-hover:scale-110" />
                        {hasFieldsToSign ? (
                          <>
                            Next Field ({remainingFields} left)
                          </>
                        ) : (
                          'Signature Requests'
                        )}
                      </button>
                    );
                  })()}
                  
                  {/* Version History Button */}
                  <button
                    onClick={() => setShowVersionHistory(true)}
                    className="px-4 py-2 rounded-xl font-medium transition-all duration-300 ease-out border group flex items-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))',
                      color: '#374151',
                      borderColor: '#e5e7eb',
                      fontWeight: 500,
                      letterSpacing: '-0.01em',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                      fontSize: '14px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, rgba(248, 250, 252, 0.9), rgba(241, 245, 249, 0.8))';
                      e.target.style.color = '#1f2937';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))';
                      e.target.style.color = '#374151';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
                    }}
                    title="View document version history"
                  >
                    <History size={16} className="mr-2 transition-transform duration-300 group-hover:scale-110" />
                    Version History
                  </button>


                  {/* Text Location Button */}
                  {/* {mode !== 'text-location' && (
                    <button
                      onClick={() => navigate(`/document/${id}/text-location`)}
                      className="px-4 py-2 rounded-xl font-medium transition-all duration-300 ease-out border group flex items-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(99, 102, 241, 0.05))',
                        color: '#6366f1',
                        borderColor: '#c7d2fe',
                        fontWeight: 500,
                        letterSpacing: '-0.01em',
                        boxShadow: '0 1px 3px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(99, 102, 241, 0.08))';
                        e.target.style.color = '#4f46e5';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.borderColor = '#a5b4fc';
                        e.target.style.boxShadow = '0 2px 8px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(99, 102, 241, 0.05))';
                        e.target.style.color = '#6366f1';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.borderColor = '#c7d2fe';
                        e.target.style.boxShadow = '0 1px 3px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
                      }}
                      title="Search for text and create form fields"
                    >
                      <FileText size={16} className="mr-2 transition-transform duration-300 group-hover:scale-110" />
                      Text Location
                    </button>
                  )} */}
                  
                  {/* Download PDF Button */}
                  {(() => {
                    const remainingFields = calculateRemainingFields();
                    const allSignaturesCompleted = remainingFields === 0 && signatureFieldsInfo && signatureFieldsInfo.currentUserFields && signatureFieldsInfo.currentUserFields.length > 0;
                    
                    return (
                      <button
                        onClick={handleDownloadDocument}
                        className="px-4 py-2 rounded-xl font-medium transition-all duration-300 ease-out border group flex items-center"
                        style={{
                          background: allSignaturesCompleted 
                            ? 'linear-gradient(135deg, rgb(52, 199, 89), rgb(46, 179, 79))'
                            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))',
                          color: allSignaturesCompleted ? '#ffffff' : '#374151',
                          borderColor: allSignaturesCompleted ? 'rgb(52, 199, 89)' : '#e5e7eb',
                          fontWeight: 500,
                          letterSpacing: '-0.01em',
                          boxShadow: allSignaturesCompleted 
                            ? '0 2px 8px rgba(52, 199, 89, 0.15)'
                            : '0 1px 3px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                          textShadow: allSignaturesCompleted ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => {
                          if (allSignaturesCompleted) {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 16px rgba(52, 199, 89, 0.2)';
                            e.target.style.filter = 'brightness(1.05)';
                          } else {
                            e.target.style.background = 'linear-gradient(135deg, rgba(248, 250, 252, 0.9), rgba(241, 245, 249, 0.8))';
                            e.target.style.color = '#1f2937';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (allSignaturesCompleted) {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 8px rgba(52, 199, 89, 0.15)';
                            e.target.style.filter = 'brightness(1)';
                          } else {
                            e.target.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))';
                            e.target.style.color = '#374151';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
                          }
                        }}
                        title={allSignaturesCompleted 
                          ? "Download completed PDF with all signatures" 
                          : "Download PDF with embedded signatures and form fields"
                        }
                      >
                        <Download size={16} className="mr-2 transition-transform duration-300 group-hover:translate-y-0.5" />
                        Download PDF
                      </button>
                    );
                  })()}
                  
                  {/* Void Document Button - Only show for owner and if not completed or voided */}
                  {document?.isOwner && document?.status !== 'completed' && document?.status !== 'voided' && (
                    <button
                      onClick={() => setShowVoidModal(true)}
                      className="px-4 py-2 rounded-xl font-medium transition-all duration-300 ease-out border group flex items-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(220, 38, 38, 0.05))',
                        color: '#dc2626',
                        borderColor: '#fecaca',
                        fontWeight: 500,
                        letterSpacing: '-0.01em',
                        boxShadow: '0 1px 3px rgba(220, 38, 38, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgba(220, 38, 38, 0.15), rgba(220, 38, 38, 0.08))';
                        e.target.style.color = '#b91c1c';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.borderColor = '#fca5a5';
                        e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(220, 38, 38, 0.05))';
                        e.target.style.color = '#dc2626';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.borderColor = '#fecaca';
                        e.target.style.boxShadow = '0 1px 3px rgba(220, 38, 38, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
                      }}
                      title="Void this document and prevent further signing"
                    >
                      <XCircle size={16} className="mr-2 transition-transform duration-300 group-hover:scale-110" />
                      Void Document
                    </button>
                  )}
                  
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="container mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className={`${showAccessControl ? 'md:col-span-2' : 'md:col-span-3'}`}>
            {showVersionHistory && (
              <DocumentVersionHistory 
                documentId={id}
                onClose={() => setShowVersionHistory(false)}
                onRestoreVersion={handleRestoreVersion}
              />
            )}
            
            {isEditing && (
              <DocumentEditor 
                documentId={id}
                onClose={() => setIsEditing(false)}
                onSave={handleSaveDocument}
              />
            )}
            
            <div className={`${(!isEditing && !showVersionHistory) ? 'block' : 'hidden'} flex justify-center`}>
              <div className="w-full max-w-4xl">
                {mode === 'text-location' ? (
                  <SimpleTextSearchViewer 
                    documentId={id}
                    document={document}
                    onBack={() => navigate(`/document/${id}`)}
                  />
                ) : (
                  <DocumentViewer 
                    documentId={id}
                    canView={canView}
                    canEdit={canEdit}
                    canSign={canSign}
                    canComment={false}
                    isOwner={document?.isOwner || false}
                    sharePermission={document?.permission || 'view'}
                    onEditClick={() => setIsEditing(true)}
                    onVersionHistoryClick={() => setShowVersionHistory(true)}
                    refreshTrigger={annotationRefreshTrigger}
                    showSignatures={showSignatures}
                    showSignatureRequests={showSignatureRequests}
                    onSignatureViewClose={() => {
                      setShowSignatures(false);
                      setShowSignatureRequests(false);
                    }}
                    onSignatureFieldsInfoUpdate={setSignatureFieldsInfo}
                    calculateRemainingFields={calculateRemainingFields}
                    navigateToNextField={navigateToNextField}
                    isHeaderVisible={isHeaderVisible}
                  />
                )}
              </div>
            </div>
          </div>
          
          {showAccessControl && !isEditing && !showVersionHistory && (
            <div className="md:col-span-1">
              <DocumentAccessControl 
                documentId={id} 
                documentName={document?.name || 'Untitled Document'} 
              />
            </div>
          )}
        </div>
      </div>

      {/* Void Document Modal */}
      {showVoidModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 m-4 max-w-md w-full">
            <div className="flex items-center mb-4">
              <XCircle className="h-6 w-6 text-red-500 mr-2" />
              <h3 className="text-lg font-medium">Void Document</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              Are you sure you want to void this document? This action will:
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                <li>Prevent any further signing by all parties</li>
                <li>Add a "VOID" watermark to all pages</li>
                <li>Send email notifications to all parties</li>
                <li>Mark the document as cancelled</li>
              </ul>
            </div>
            
            <div className="mb-4">
              <label htmlFor="voidReason" className="block text-sm font-medium text-gray-700 mb-2">
                Reason for voiding (required):
              </label>
              <textarea
                id="voidReason"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                rows="3"
                placeholder="e.g., Terms have changed, document contains errors, etc."
                disabled={isVoiding}
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowVoidModal(false);
                  setVoidReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                disabled={isVoiding}
              >
                Cancel
              </button>
              <button
                onClick={handleVoidDocument}
                disabled={isVoiding || !voidReason.trim()}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isVoiding ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Voiding...
                  </>
                ) : (
                  'Void Document'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Redlining Analysis Modal */}
      {showRedliningPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 m-4 max-w-md w-full">
            <div className="flex items-center mb-4">
              <Zap className="h-6 w-6 text-yellow-500 mr-2" />
              <h3 className="text-lg font-medium">AI Document Analysis</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Our AI will analyze this document to identify potential risks, problematic clauses, 
              and areas that may need your attention before signing.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-6">
              <div className="flex items-start">
                <Eye className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <strong>What we'll analyze:</strong>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    <li>Contract terms and conditions</li>
                    <li>Potential legal risks</li>
                    <li>Unusual or concerning clauses</li>
                    <li>Areas requiring attention</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowRedliningPrompt(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                disabled={isAnalyzing}
              >
                Cancel
              </button>
              <button
                onClick={handleAiAnalysis}
                disabled={isAnalyzing}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAnalyzing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-1" />
                    Start Analysis
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Simple text search viewer component
const SimpleTextSearchViewer = ({ documentId, document, onBack }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale] = useState(1.0);
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const containerRef = useRef(null);
  const [visualFields, setVisualFields] = useState([]);
  const pageRef = useRef(null);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 595.44, height: 842.4 }); // Default fallback
  const [showDimensionFallbackWarning, setShowDimensionFallbackWarning] = useState(false);

  // Calculate absolute position for visual markers
  const getAbsolutePosition = (x, y, width, height) => {
    if (!pageRef.current) return { left: 0, top: 0, width: 0, height: 0 };
    
    const pageElement = pageRef.current.querySelector('.react-pdf__Page__canvas');
    if (!pageElement) return { left: 0, top: 0, width: 0, height: 0 };
    
    const pageRect = pageElement.getBoundingClientRect();
    const containerRect = pageRef.current.getBoundingClientRect();
    
    // Convert PDF coordinates to pixel coordinates using auto-detected dimensions
    const pixelX = (x / pdfDimensions.width) * pageRect.width;
    const pixelY = (y / pdfDimensions.height) * pageRect.height;
    const pixelWidth = (width / pdfDimensions.width) * pageRect.width;
    const pixelHeight = (height / pdfDimensions.height) * pageRect.height;
    
    // Adjust Y position to render higher up by reducing the top offset
    const adjustedY = pixelY - 25; // Move up by 50 pixels
    
    return {
      left: pixelX,
      top: Math.max(0, adjustedY), // Ensure it doesn't go above the page
      width: pixelWidth,
      height: pixelHeight
    };
  };

  // Load document - using same logic as DocumentViewer
  useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoading(true);
        console.log('📄 Loading document for text search:', documentId);
        
        // Load document data (same as DocumentViewer)
        const documentData = await documentService.getDocument(documentId);
        if (!documentData) {
          throw new Error('Document not found or access denied');
        }
        
        console.log('📄 Document data loaded:', documentData);
        
        // Fetch the file as a blob (same as DocumentViewer)
        const fileUrl = `${API_URL}/documents/${documentId}/file?skipEmbed=true`;
        console.log('📄 Fetching document file from:', fileUrl);
        
        const authToken = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).token : null;
        const headers = {};
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
        }
        
        const response = await axios.get(fileUrl, {
          responseType: 'blob',
          headers
        });
        
        console.log('📄 File fetch successful, content type:', response.headers['content-type']);
        
        // Create blob URL (same as DocumentViewer)
        const contentType = response.headers['content-type'] || 'application/pdf';
        const fileBlob = new Blob([response.data], { type: contentType });
        const fileObjectUrl = URL.createObjectURL(fileBlob);
        
        console.log('📄 Created blob URL:', fileObjectUrl);
        
        // Clean up previous blob URL
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
        }
        
        setBlobUrl(fileObjectUrl);
        setFileUrl(fileObjectUrl);
        setPdfError(null);
        setLoading(false);
        
      } catch (error) {
        console.error('❌ Error loading document:', error);
        if (error.response) {
          console.error('Server returned error status:', error.response.status);
          console.error('Server error details:', error.response.data);
        }
        setPdfError('Failed to load PDF document');
        setLoading(false);
      }
    };

    loadDocument();
    
    // Cleanup function to revoke blob URL when component unmounts
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [documentId]);

  // Text search handler
  const handleTextSearch = async () => {
    console.log('🔍 DocumentPage handleTextSearch called!');
    console.log('🔍 searchText:', searchText);
    console.log('🔍 isSearching:', isSearching);

    if (!searchText.trim() || isSearching) {
      console.log('🔍 Early return - no text or already searching');
      return;
    }

    setIsSearching(true);
    setSearchResults(null);
    setVisualFields([]); // Clear previous visual fields

    try {
      console.log(`🔍 Searching for text: "${searchText}"`);

      const response = await findTextLocation(documentId, searchText);

      if (response.data.success) {
        console.log('📊 DocumentPage: Text search response:', response.data);
        console.log('📊 DocumentPage: formFields:', response.data.formFields);
        console.log('📊 DocumentPage: matches:', response.data.matches);

        setSearchResults(response.data);
        setShowSearchResults(true);

        // Navigate to the page where the text was found
        if (response.data.selectedMatch) {
          setPageNumber(response.data.selectedMatch.page);
        }

        // Create visual form fields from the search response
        if (response.data.formFields && response.data.formFields.length > 0) {
          console.log(`📊 Creating ${response.data.formFields.length} visual form fields from text search`);

          // Collect all visual fields first
          const newVisualFields = [];

          response.data.formFields.forEach((formField, index) => {
            console.log(`📊 Processing form field ${index + 1}:`, formField);
            console.log(`📊 Form field coordinates:`, formField.coordinates);

            // Create a visual form field component with minimum size constraints
            const minWidth = 120; // Minimum width for visibility and usability
            const minHeight = 25; // Minimum height for visibility and usability
            
            const visualField = {
              id: `text-search-${Date.now()}-${index}`,
              type: 'form_field',
              fieldType: formField.fieldType || 'text',
              fieldId: formField.fieldId || `text-location-${Date.now()}-${index}`,
              page: formField.coordinates.page,
              x: formField.coordinates.x + (index * 30), // Spread out overlapping fields
              y: formField.coordinates.y + (index * 30), // Spread out overlapping fields
              width: Math.max(formField.coordinates.width || minWidth, minWidth),
              height: Math.max(formField.coordinates.height || minHeight, minHeight),
              placeholder: formField.placeholder || searchText,
              required: formField.required || false,
              fieldValue: '', // Empty initially
              conceptual: true, // Mark as conceptual for development
              originalWidth: formField.coordinates.width, // Store original for reference
              originalHeight: formField.coordinates.height
            };

            console.log(`✅ Created visual form field ${index + 1}:`, visualField);
            newVisualFields.push(visualField);
          });

          // Store visual fields in local state for rendering
          setVisualFields(newVisualFields);
          console.log(`✅ Successfully created ${response.data.formFields.length} visual form fields from text search`);
        } else {
          console.log('📊 No formFields in response to display');
          setVisualFields([]); // Clear any existing fields
        }
      }
    } catch (error) {
      console.error('❌ Error searching for text:', error);
      if (error.response?.status === 404) {
        setSearchResults({ error: 'Text not found', message: `The text "${searchText}" was not found in the document` });
      } else {
        setSearchResults({ error: 'Search failed', message: 'Failed to search for text in the document' });
      }
      setShowSearchResults(true);
    } finally {
      setIsSearching(false);
    }
  };

  // PDF event handlers
  const onDocumentLoadSuccess = (pdf) => {
    setNumPages(pdf.numPages);
    setPdfError(null);
    console.log('✅ PDF loaded successfully with', pdf.numPages, 'pages');
    
    // Get the first page to extract dimensions
    pdf.getPage(1).then(page => {
      const viewport = page.getViewport({ scale: 1.0 });
      const detectedDimensions = {
        width: viewport.width,
        height: viewport.height
      };
      
      console.log('📏 Auto-detected PDF dimensions:', detectedDimensions);
      setPdfDimensions(detectedDimensions);
    }).catch(error => {
      console.warn('⚠️ Could not auto-detect PDF dimensions, using fallback:', error);
      // Keep the default fallback dimensions and show warning
      setShowDimensionFallbackWarning(true);
    });
  };

  const onDocumentLoadError = (error) => {
    console.error('❌ PDF load error:', error);
    setPdfError('Failed to load PDF. The document may be corrupted or not a valid PDF file.');
  };

  const onPageLoadSuccess = () => {
    console.log('✅ Page loaded successfully');
  };

  const onPageLoadError = (error) => {
    console.error('❌ Page load error:', error);
    setPdfError('Failed to load page. The document may be corrupted.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft size={20} className="mr-2" />
              Back to Document
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Text Search</h1>
          </div>
          
          {/* Search Controls */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleTextSearch()}
                placeholder="Search for text..."
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isSearching}
              />
              <button
                onClick={handleTextSearch}
                disabled={!searchText.trim() || isSearching}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  !searchText.trim() || isSearching
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isSearching ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  'Search'
                )}
              </button>
              {visualFields.length > 0 && (
                <button
                  onClick={() => {
                    setVisualFields([]);
                    setSearchResults(null);
                    setShowSearchResults(false);
                  }}
                  className="px-3 py-2 rounded-lg font-medium transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200"
                  title="Clear visual markers"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Search Results */}
      {showSearchResults && searchResults && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          {searchResults.error ? (
            <div className="flex items-center space-x-2 text-red-600">
              <AlertCircle size={16} />
              <span className="text-sm">{searchResults.message}</span>
              <button
                onClick={() => setShowSearchResults(false)}
                className="ml-auto text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-green-600">
                <Check size={16} />
                <span className="text-sm">{searchResults.message}</span>
                {searchResults.matches && searchResults.matches.length > 1 && (
                  <span className="text-xs text-gray-500">
                    ({searchResults.matches.length} matches found)
                  </span>
                )}
                {visualFields.length > 0 && (
                  <span className="text-xs text-blue-600 ml-2 bg-blue-50 px-2 py-1 rounded">
                    {visualFields.length} visual field{visualFields.length !== 1 ? 's' : ''} created
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowSearchResults(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Document Viewer */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div ref={containerRef} className="flex justify-center relative">
          {fileUrl && !pdfError ? (
            <div ref={pageRef} className="relative">
              <Document
                file={fileUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                }
                error={
                  <div className="flex items-center justify-center h-96 text-red-600">
                    <AlertCircle size={24} className="mr-2" />
                    Failed to load document
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  onLoadSuccess={onPageLoadSuccess}
                  onLoadError={onPageLoadError}
                  loading={
                    <div className="flex items-center justify-center h-96">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  }
                />
              </Document>
              
              {/* Visual Field Overlays */}
              {visualFields.length > 0 && (
                <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 25 }}>
                  {visualFields
                    .filter(field => field.page === pageNumber)
                    .map((field, index) => {
                      const position = getAbsolutePosition(field.x, field.y, field.width, field.height);
                      
                      return (
                        <div
                          key={`visual-field-${field.id}-${index}`}
                          className="absolute border-2 border-blue-500 bg-blue-100 bg-opacity-50 rounded-lg flex items-center justify-center pointer-events-auto cursor-pointer hover:bg-opacity-70 transition-all duration-200"
                          style={{
                            left: `${position.left}px`,
                            top: `${position.top}px`,
                            width: `${position.width}px`,
                            height: `${position.height}px`,
                            zIndex: 25
                          }}
                          title={`Form field: ${field.fieldType} - ${field.placeholder}${
                            (field.originalWidth && field.originalWidth < 120) || (field.originalHeight && field.originalHeight < 25) 
                              ? ' (expanded to minimum size for visibility)' 
                              : ''
                          }`}
                        >
                          <div className="flex items-center justify-center space-x-1 text-blue-700 text-xs font-medium">
                            <FileText size={12} />
                            <span className="truncate">{field.fieldType}</span>
                            {((field.originalWidth && field.originalWidth < 120) || (field.originalHeight && field.originalHeight < 25)) && (
                              <span className="text-blue-500" title="Expanded to minimum size">↗</span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              )}
            </div>
          ) : pdfError ? (
            <div className="flex flex-col items-center justify-center h-96 text-red-600 p-8">
              <AlertCircle size={48} className="mb-4" />
              <h3 className="text-lg font-semibold mb-2">PDF Loading Error</h3>
              <p className="text-sm text-center mb-4">{pdfError}</p>
              <div className="text-xs text-gray-500 text-center">
                <p>This may be due to:</p>
                <ul className="list-disc list-inside mt-2">
                  <li>Corrupted or incomplete PDF file</li>
                  <li>Invalid PDF format</li>
                  <li>Network connectivity issues</li>
                  <li>File access permissions</li>
                </ul>
              </div>
              <div className="mt-4 flex space-x-2">
                <button
                  onClick={() => window.open(fileUrl, '_blank')}
                  className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  Open in New Tab
                </button>
                <button
                  onClick={() => {
                    setPdfError(null);
                    // Force reload by clearing and resetting fileUrl
                    setFileUrl(null);
                    setTimeout(() => {
                      const response = documentService.getDocument(documentId).then(r => {
                        setFileUrl(r.fileUrl || r.downloadUrl);
                      });
                    }, 100);
                  }}
                  className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-96 text-gray-500">
              <AlertCircle size={24} className="mr-2" />
              No document URL available
            </div>
          )}
        </div>

        {/* Page Navigation */}
        {numPages && numPages > 1 && (
          <div className="flex items-center justify-center space-x-4 p-4 border-t border-gray-200">
            <button
              onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
              disabled={pageNumber <= 1}
              className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:text-gray-400"
            >
              <ChevronLeft size={16} className="mr-1" />
              Previous
            </button>
            
            <span className="text-sm text-gray-600">
              Page {pageNumber} of {numPages}
            </span>
            
            <button
              onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
              disabled={pageNumber >= numPages}
              className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:text-gray-400"
            >
              Next
              <ChevronRight size={16} className="ml-1" />
            </button>
          </div>
        )}
      </div>

      {/* Dimension Fallback Warning Modal */}
      {showDimensionFallbackWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 m-4 max-w-md w-full shadow-xl">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-yellow-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">PDF Dimensions Detection</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              Unable to automatically detect this PDF's dimensions. Using standard A4 fallback dimensions 
              (595.44 × 842.4 points). Visual field positioning may be less accurate.
            </p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
              <div className="text-sm text-yellow-700">
                <strong>What this means:</strong>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Text search will still work</li>
                  <li>Visual markers may be slightly misaligned</li>
                  <li>You can still see where fields would be placed</li>
                </ul>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowDimensionFallbackWarning(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentPage;